import React from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { listEnabledSpecialties, CM7_SETTINGS } from "features/chartMind7/data/chartMind7PromptRegistry";
import {
  chartMind7SettingOptionLabel,
  chartMind7SpecialtyOptionLabel,
  resolveChartMind7PromptChoice,
} from "../chartMind7PromptGroups";

const PromptCard = ({
  prompt,
  onEdit,
  readOnly = false,
  showFusionEditToggle = false,
  fusionEditAllowed = true,
  fusionEditSaving = false,
  fusionEditReady = true,
  fusionEditSuccess = false,
  fusionEditError = "",
  onFusionEditChange,
  onDismissFusionEditSuccess,
  onDismissFusionEditError,
}) => {
  const title = prompt.featureName || prompt.id;
  const combinations = prompt.combinations || [];
  const showCombinations = combinations.length > 0;
  const { control } = useForm({
    defaultValues: { specialtyKey: "", setting: "" },
  });
  const specialtyKey = useWatch({ control, name: "specialtyKey" });
  const setting = useWatch({ control, name: "setting" });
  const choice = resolveChartMind7PromptChoice(prompt, specialtyKey, setting);
  const incomplete = choice.status === "incomplete";

  return (
    <Card
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
      }}
    >
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
          {showCombinations ? ` · ${prompt.pendingCount || 0} pending` : ""}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{
            mt: 0.25,
            fontFamily: "ui-monospace, monospace",
            letterSpacing: "0.02em",
          }}
        >
          {prompt.id}
        </Typography>
        {showCombinations ? (
          <Box sx={{ display: "grid", gap: 1.5, mt: 1.5 }}>
            <Controller
              name="specialtyKey"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Specialty"
                  inputProps={{ "data-testid": `chartMind7.queryManager.specialty.${prompt.id}` }}
                >
                  <MenuItem value="">Default (all specialties)</MenuItem>
                  {listEnabledSpecialties().map((specialty) => (
                    <MenuItem key={specialty.key} value={specialty.key}>
                      {chartMind7SpecialtyOptionLabel(specialty, combinations)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="setting"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Setting"
                  inputProps={{ "data-testid": `chartMind7.queryManager.setting.${prompt.id}` }}
                >
                  <MenuItem value="">Default (all settings)</MenuItem>
                  {CM7_SETTINGS.map((item) => (
                    <MenuItem key={item} value={item}>
                      {chartMind7SettingOptionLabel(item, specialtyKey, combinations)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {incomplete ? (
              <Typography variant="body2" color="text.secondary">
                Choose both
              </Typography>
            ) : null}
          </Box>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
          {prompt.featureDescription || "No description provided."}
        </Typography>
        {showFusionEditToggle ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                sx={{ mr: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    checked={fusionEditAllowed}
                    disabled={fusionEditSaving || !fusionEditReady}
                    onChange={(_, checked) => onFusionEditChange?.(checked)}
                    inputProps={{
                      "aria-label": "Allow Fusion Partners to edit this query",
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    Allow Fusion Partners to edit this query
                  </Typography>
                }
              />
              {fusionEditSaving || !fusionEditReady ? (
                <CircularProgress size={16} />
              ) : null}
            </Box>
            {fusionEditSuccess ? (
              <Alert
                severity="success"
                sx={{ mt: 1 }}
                onClose={onDismissFusionEditSuccess}
              >
                Fusion Partner edit permission saved.
              </Alert>
            ) : null}
            {fusionEditError ? (
              <Alert
                severity="error"
                sx={{ mt: 1 }}
                onClose={onDismissFusionEditError}
              >
                {fusionEditError}
              </Alert>
            ) : null}
          </Box>
        ) : null}
      </CardContent>
      {!readOnly ? (
        <CardActions>
          <LoadingButton
            size="small"
            disabled={incomplete}
            onClick={() => onEdit(choice.prompt || prompt)}
          >
            Edit Prompt
          </LoadingButton>
        </CardActions>
      ) : null}
    </Card>
  );
};

export default PromptCard;
